import React, { useMemo } from 'react';
import HashtagLabs from '@/components/HashtagLabs';
import { Box, Grid, Typography, Stack, Button } from '@mui/material';
import HubspotForm from '@/components/HubspotForm';
import { useSearchParams } from 'next/navigation';
import useGlobalQuery from '@orgnc/core/hooks/useGlobalQuery';
import advertiseTemplateDetailsQuery from '@/lib/queries/advertiseTemplateDetailsQuery';
import addImageRight from '@/../public/images/add-template-Image-right.webp';
import addImageLeft from '@/../public/images/add-template-Image-left.webp';
import contactUsImage from '@/../public/images/add-template-image-contact-us.webp';

interface UTMEntry {
  phonenumberHtml: string;
  utmCampaign: string;
  utmSource: string;
}

const AdvertisePageTemplate: React.FC = () => {
  const { data: advertiseTemplateConfiguration } = useGlobalQuery(
    advertiseTemplateDetailsQuery
  );

  const utmTrackingEntries =
    advertiseTemplateConfiguration?.acfOptionsGeneral?.generalOptions
      ?.advertiseTemplateConfiguration?.utmTrackingEntries ?? [];

  const hubspotConfiguration =
    advertiseTemplateConfiguration?.acfOptionsGeneral?.generalOptions
      ?.hubspotConfiguration;

  const siteLogo =
    advertiseTemplateConfiguration?.acfOptionsGeneral?.generalOptions
      ?.pageContent?.siteLogo;

  const searchParams = useSearchParams();

  // Get URL params
  const utmSource = searchParams.get('utm_source');
  const utmCampaign = searchParams.get('utm_campaign');

  // Match based on all three fields
  const cleanEntries = utmTrackingEntries.filter(Boolean) as UTMEntry[];

  const matchedPhone = useMemo(() => {
    // Step 1: Try to find an exact match
    const exactMatch = cleanEntries.find(
      (entry) =>
        entry.utmSource === utmSource && entry.utmCampaign === utmCampaign
    );

    if (exactMatch) return exactMatch;

    // Step 2: Fallback - match specific utmSource
    const fallbackMatch = cleanEntries.find(
      (entry) => entry.utmSource === 'main_sales' && !entry.utmCampaign
    );

    return fallbackMatch || null;
  }, [cleanEntries, utmSource, utmCampaign]);

  return (
    <>
      <HashtagLabs />
      <Box
        bgcolor="#fff"
        sx={{
          '& .formGridSection': {
            backgroundColor: '#4b4f58',
            padding: '20px !important',
            marginTop: '30px',
            borderRadius: '10px',
            '@media (max-width:900px)': {
              marginLeft: '32px',
            },
          },
          '& .grid_title': {
            fontSize: '36.7px',
            fontWeight: '700',
            lineHeight: '45.6px',
            marginBottom: '36px',
            '@media (max-width:575px)': {
              fontSize: '24.9px',
              lineHeight: '31.2px',
            },
          },
          '& .grid_subtitle': {
            fontSize: '21.1px',
            fontWeight: '700',
            lineHeight: '26.4px',
            '@media (max-width:575px)': {
              fontSize: '19.2px',
              lineHeight: '24px',
            },
          },
          '& .grid_bodyText': {
            fontSize: '20.0px',
            lineHeight: '26.4px',
            '@media (max-width:575px)': {
              fontSize: '18.3px',
              lineHeight: '24px',
            },
          },
          '& .box_wrap': {
            marginBottom: '32px !important',
            marginTop: '0px !important',
          },
          '& .section_spacing': {
            marginBottom: '80px',
            '@media (max-width:575px)': {
              marginBottom: '60px',
            },
          },
          '& .my_30': {
            marginTop: '30px',
            marginBottom: '30px',
          },
          '& .Box_image': {
            width: '100%',
            height: '580px',
            borderRadius: '10px',
            objectFit: 'cover',
            '@media (max-width:575px)': {
              height: 'auto',
            },
          },
          '& .main_title': {
            fontSize: '51.4px',
            fontWeight: '700',
            lineHeight: '53px',
            marginBottom: '20px',
            '@media (max-width:575px)': {
              fontSize: '42px',
              lineHeight: '44px',
            },
          },
          '& .form_title': {
            fontSize: '27px',
            fontWeight: '700',
            lineHeight: '33.6px',
            marginBottom: '20px',
            color: '#FFF',
          },
          '& .logo_wrap': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '@media (max-width:575px)': {
              display: 'none',
            },
          },
          '& .logo_image': {
            textAlign: 'center',
            height: 'auto',
            width: 'auto',
          },
          '& .mobile_logo': {
            display: 'none',
            '@media (max-width:575px)': {
              display: 'block',
            },
          },
          '& .contact_us_section': {
            backgroundColor: '#000',
            borderRadius: '15px',
            padding: '25px',
            color: '#fff',
          },
          '& .contact_us_section a': {
            backgroundColor: 'transparent',
            borderRadius: '100px',
            border: '2px solid #fff',
            color: '#fff',
            fontWeight: '600',
            fontSize: '17.3px',
            lineHeight: '21.4px',
            padding: '10px 20px',
          },
          '& .contact_us_section .grid_title': {
            marginBottom: '20px',
          },
          '& .contact_us_section .grid_bodyText': {
            marginBottom: '40px',
          },
          '& .contact_image': {
            display: 'none',
            '@media (max-width:575px)': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              marginBottom: '40px',
            },
          },
          '& .contact_image img': {
            width: '100%',
          },
          p: { xs: 2, sm: 4 },
        }}
      >
        <Grid
          container
          spacing={4}
          alignItems="start"
          className="section_spacing"
        >
          {/* Image Section */}
          <Grid item xs={12} md={7}>
            <Typography variant="h1" fontWeight="bold" className="main_title">
              Your Brand. Our Audience. Unbeatable Results.
            </Typography>
            <Typography variant="body1" className="grid_bodyText">
              Ready to take your business to the next level? With our powerful
              combination of radio, streaming audio, and targeted digital
              solutions, your brand gets noticed by the audiences that matter
              most. Whether you want to reach listeners during their morning
              commute, connect with fans online, or engage customers across
              multiple platforms, we have the tools and expertise to deliver
              real results. Partner with us and discover how easy it is to grow
              your business with the power of Beasley’s trusted media network.
            </Typography>
            <Typography variant="body1" className="grid_bodyText my_30">
              <b>
                <i> We turn local businesses into household names</i>
              </b>
            </Typography>
            {matchedPhone?.phonenumberHtml && (
              <Typography variant="h3" fontWeight="bold" className="grid_title">
                Contact our sales team:
                <Typography className="grid_title">
                  {matchedPhone.phonenumberHtml}
                </Typography>
              </Typography>
            )}
            {siteLogo?.mediaItemUrl && (
              <Box className="logo_wrap">
                <img
                  src={siteLogo.mediaItemUrl}
                  alt={siteLogo.altText || 'Site Logo'}
                  title={siteLogo.altText || ''}
                  className="logo_image"
                />
              </Box>
            )}
          </Grid>

          {/* Text Content Section */}
          <Grid item xs={12} className="formGridSection" md={5}>
            <Typography variant="h1" fontWeight="bold" className="form_title">
              {hubspotConfiguration?.hubspotFormTitle ??
                'Ready to grow your business?'}
            </Typography>
            <HubspotForm
              portalId={hubspotConfiguration?.hubspotPortalId ?? ''}
              formId={hubspotConfiguration?.hubspotFormId ?? ''}
              region={hubspotConfiguration?.hubspotRegion ?? 'na1'}
            />
            {siteLogo?.mediaItemUrl && (
              <Box className="logo_wrap mobile_logo">
                <img
                  src={siteLogo?.mediaItemUrl || ''}
                  alt={siteLogo?.altText || 'Site Logo'}
                  title={siteLogo?.altText || ''}
                  // width={1}
                  // height={1}
                  className="logo_image"
                />
              </Box>
            )}
          </Grid>
        </Grid>
        <Grid
          container
          spacing={4}
          alignItems="start"
          className="section_spacing"
        >
          {/* Image Section */}
          <Grid item xs={12} md={5}>
            <Box borderRadius={2} overflow="hidden">
              <img
                src={addImageRight.src} // Replace with actual image path
                alt="Woman stretching with headphones"
                style={{ width: '100%', height: '580px', objectFit: 'cover' }}
                className="Box_image"
              />
            </Box>
          </Grid>

          {/* Text Content Section */}
          <Grid item xs={12} md={7}>
            <Stack spacing={3}>
              <Typography variant="h3" fontWeight="bold" className="grid_title">
                Advertising solutions that drive results
              </Typography>

              <Box className="box_wrap">
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  className="grid_subtitle"
                >
                  Drive impact with audio
                </Typography>
                <Typography variant="body1" className="grid_bodyText">
                  Audio is a core element of consumer culture—which means it
                  should be a core part of your marketing plan. Listening
                  minutes are as high as they’ve ever been (and your ROI will
                  follow suit).
                </Typography>
              </Box>

              <Box className="box_wrap">
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  className="grid_subtitle"
                >
                  Access the largest ad-supported audience
                </Typography>
                <Typography variant="body1" className="grid_bodyText">
                  Between Pandora, SoundCloud, and SiriusXM (as well as our
                  streaming and podcast networks), we offer the biggest
                  addressable listener base in the country.
                </Typography>
              </Box>

              <Box className="box_wrap">
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  className="grid_subtitle"
                >
                  Campaigns that deliver
                </Typography>
                <Typography variant="body1" className="grid_bodyText">
                  With partners like Veritonic, FourSquare, ArtsAI, and
                  Comscore, we’re here to make it easy to plan, optimize, and
                  validate your marketing strategies and measure the results
                  that matter.
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={4}
          alignItems="start"
          className="section_spacing"
        >
          {/* Image Section */}
          <Grid item xs={12} md={5} style={{ order: 2 }}>
            <Box borderRadius={2} overflow="hidden">
              <img
                src={addImageLeft.src} // Replace with actual image path
                alt="Woman stretching with headphones"
                style={{ width: '100%', height: '580px', objectFit: 'cover' }}
                className="Box_image"
              />
            </Box>
          </Grid>

          {/* Text Content Section */}
          <Grid item xs={12} md={7} order={{ xs: 2, md: 1 }}>
            <Stack spacing={3}>
              <Typography variant="h3" fontWeight="bold" className="grid_title">
                Premium platforms, premium service
              </Typography>

              <Box className="box_wrap">
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  className="grid_subtitle"
                >
                  Best-in-class customer service
                </Typography>
                <Typography variant="body1" className="grid_bodyText">
                  Our content isn’t the only thing you’ll love; our audio
                  experts work with you to build a custom campaign that fits
                  your needs—from ideation to execution to measurement.
                </Typography>
              </Box>

              <Box className="box_wrap">
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  className="grid_subtitle"
                >
                  End-to-end solutions
                </Typography>
                <Typography variant="body1" className="grid_bodyText">
                  From audio to display to video, our expansive product suite
                  gives you options to tell your brand story, your way.
                </Typography>
              </Box>

              <Box className="box_wrap">
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  className="grid_subtitle"
                >
                  In-house creative services
                </Typography>
                <Typography variant="body1" className="grid_bodyText">
                  No audio ad? No problem. Work with our award-winning in-house
                  creative studio to produce your high-touch, high-impact audio
                  creative.
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
        <Grid
          container
          alignItems="start"
          className="section_spacing contact_us_section"
        >
          {/* Text Content Section */}
          <Grid item xs={12} md={5} order={{ xs: 2, md: 1 }}>
            <Typography variant="h3" fontWeight="bold" className="grid_title">
              Ready to reach your audience with audio?
            </Typography>
            <Typography variant="body1" className="grid_bodyText">
              Talk to a SiriusXM Media sales representative and build your
              custom audio plan.
            </Typography>
            <Button component="a" href="#">
              Contact us
            </Button>
          </Grid>

          {/* Image Section */}
          <Grid item xs={12} md={7} sx={{ order: { xs: 1, md: 2 } }}>
            <Box className="logo_wrap contact_image">
              <img
                src={contactUsImage.src} // Replace with actual image path
                alt="Woman stretching with headphones"
                style={{ width: '100%', height: 'auto' }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default AdvertisePageTemplate;
